import React, { useEffect, useMemo, useState } from 'react';
import { ChevronDown } from 'react-feather';
import { UseFormReturn } from 'react-hook-form';
import DOMPurify from 'dompurify';
import Button from 'components/button';
import ImageCarousel from 'components/image-carousel';
import Back from 'components/back';
import { ErrorText } from 'components/typography';
import { ProviderImagesUrlSmall } from 'constants/images';
import {
  getQuestionsToDisplay,
  shouldDisable,
  trackMSDisclosureClick,
} from 'helpers';
import { IFormDefinitionPage, IFormQuestion } from 'types';
import FormEngineController from '../components/FormEngineController';
import {
  getChangeHandler,
  getClickHandler,
  getFieldComponent,
  onMountFns,
} from '../utils';
import { isRV } from 'helpers';
import { getValidator } from '../validation';
import { FormEngineCustomErrors } from '../constants';
import styles from './styles/MarketScanSummary.module.scss';
import classnames from 'classnames';
import { INSURANCE_TYPES_NAMES } from 'constants/insurance-types-names';
import Collapsible from 'components/collapsible';

const DirectImages = [
  ProviderImagesUrlSmall.AA,
  ProviderImagesUrlSmall.AMP,
  ProviderImagesUrlSmall.AUTOSURE,
  ProviderImagesUrlSmall.COVE,
  ProviderImagesUrlSmall.TOWER,
  ProviderImagesUrlSmall.PROTECTA,
  ProviderImagesUrlSmall.TRADE_ME,
  ProviderImagesUrlSmall.PROVIDENT,
];

const HomeAndContentsImages = [
  ProviderImagesUrlSmall.AA,
  ProviderImagesUrlSmall.AMP,
  ProviderImagesUrlSmall.INITIO,
  ProviderImagesUrlSmall.TRADE_ME,
  ProviderImagesUrlSmall.TOWER,
];

interface IProps {
  fieldClassName?: string;
  fieldContainerClassName?: string;
  inputClassName?: string;
  labelClassName?: string;
  options?: any;
  form?: UseFormReturn;
  loading?: boolean;
  formValues?: any;
  questionProps?: any;
  page: IFormDefinitionPage;
  getOptions?(question: IFormQuestion, formValues: any): any;
  onBack(): void;
}

interface ISectionProps extends Omit<IProps, 'page'> {
  section?: any;
}

const Section = ({
  fieldClassName,
  fieldContainerClassName,
  inputClassName,
  labelClassName,
  options,
  getOptions,
  form,
  formValues,
  section,
  questionProps,
  ...props
}: ISectionProps) => {
  const [showSection, setShowSection] = useState(true);
  const { control, formState } = form;
  const { questions, title, collapsible } = section;
  const errorKeys = Object.keys(formState.errors);

  // All questions in this component should be "basic"
  const questionsToDisplay = useMemo(() => {
    // Hide address field when user entered address manually
    const toOmit = formValues.showFullAddress ? ['address'] : [];
    return getQuestionsToDisplay(
      questions,
      formValues,
      toOmit,
      questionProps,
    ).map(R.assoc('basic', true));
  }, [questions, questionProps, formValues]);

  const toggleSection = () => setShowSection(R.not);

  return (
    <>
      {collapsible && title && (
        <div className={styles.sectionTitle} onClick={toggleSection}>
          {title} <ChevronDown />
        </div>
      )}
      {showSection && (
        <div className={styles.section}>
          {questionsToDisplay?.map((question) => {
            const {
              key,
              type,
              required,
              className: qClassName,
              disabled,
            } = question;

            const FieldComponent = getFieldComponent(type);
            const validate = getValidator(type, question, formValues);
            const error =
              formState.errors[key]?.message || formState.errors[key];
            const opt =
              (getOptions && getOptions(question, formValues)) ||
              options?.[key] ||
              question.options;

            return (
              <FormEngineController
                key={key}
                control={control}
                name={key}
                rules={{ required, validate }}
                render={({ field }) => (
                  <FieldComponent
                    {...question}
                    {...field}
                    disabled={shouldDisable(disabled, formValues)}
                    options={opt}
                    onChange={getChangeHandler(field, form, props, question)}
                    onClick={getClickHandler(question, form, props)}
                    error={error}
                    scrollIntoView={errorKeys[0] === key}
                    containerClassName={classnames(
                      styles.fieldContainer,
                      fieldContainerClassName,
                    )}
                    className={classnames(
                      styles.field,
                      qClassName,
                      fieldClassName,
                    )}
                    inputClassName={classnames(styles.input, inputClassName)}
                    labelClassName={classnames(
                      styles.fieldLabel,
                      labelClassName,
                    )}
                  />
                )}
              />
            );
          })}
        </div>
      )}
    </>
  );
};

const RightSection = ({ form, formValues, loading, page, onBack }: IProps) => {
  const { formState } = form;
  const buttonText = page.rightSection?.buttonText || 'Scan';
  const disclaimer = page.rightSection?.disclaimer;
  const notesTitle = page.rightSection?.notesTitle;
  const notes = page.rightSection?.notes;
  const msSummaryError = formState.errors?.[FormEngineCustomErrors.DISCLAIMER];
  const disableBtn = loading || !!msSummaryError;

  const images = useMemo(
    () =>
      formValues.policyType === INSURANCE_TYPES_NAMES.HOME_CONTENTS
        ? HomeAndContentsImages
        : DirectImages,
    [formValues.policyType],
  );

  useEffect(
    () => () => {
      // Clear specific component error on unmount so it does
      // not disable the submit button on the previous pages
      form.clearErrors(FormEngineCustomErrors.DISCLAIMER);
    },
    [],
  );

  const handleDisclosureClick = (e) => {
    // Only track when clicked element is a link
    if (e.target.parentElement?.tagName === 'A') {
      trackMSDisclosureClick();
    }
  };

  return (
    <div className={styles.right}>
      <Collapsible
        className={classnames(
          styles.collapsible,
          isRV(formValues.policyType) && styles.rvContainer,
        )}
        content={
          <div
            className={styles.notes}
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(notes, {
                ADD_ATTR: ['target', 'rel'],
              }),
            }}
          />
        }
        title={
          <>
            {!isRV(formValues.policyType) ? (
              <ImageCarousel className={styles.images} images={images} />
            ) : (
              <div className={styles.rvLogoImage}>
                <img alt="Star" src={ProviderImagesUrlSmall.STAR} />
              </div>
            )}

            <div className={styles.notesTitle}>
              <b
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(notesTitle),
                }}
              />
              <ChevronDown className={styles.notesChevron} />
            </div>
          </>
        }
      />

      <div className={styles.buttonContainer}>
        <h4>Ready to go</h4>
        {disclaimer && (
          <div
            className={styles.disclaimer}
            onClick={handleDisclosureClick}
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(disclaimer, {
                ADD_ATTR: ['target', 'rel'],
              }),
            }}
          />
        )}
        {msSummaryError?.message && (
          <ErrorText
            className={styles.errorDisclaimer}
            text={msSummaryError?.message}
          />
        )}
        <Button
          className={styles.submit}
          type="submit"
          disabled={disableBtn}
          loading={loading}
          text={buttonText}
        />
        <Back className={styles.back} handleBack={onBack} />
      </div>
    </div>
  );
};

const MarketScanSummary = ({ page, loading, ...props }: IProps) => {
  useEffect(() => {
    // Update form value if there is an onMountFn for a question
    page.questions.forEach((question) => {
      if (question.onMountFn) {
        const fn = onMountFns[question.onMountFn];
        const val = fn(props.formValues);
        if (!R.isNil(val)) {
          props.form.setValue(question.key, val);
        }
      }
    });
  }, []);

  return (
    <div className={classnames('market-scan-summary', styles.container)}>
      <div className={styles.left}>
        {page.sections.map((section, idx) => (
          <Section key={`section_${idx}`} {...props} section={section} />
        ))}
      </div>
      <RightSection page={page} loading={loading} {...props} />
    </div>
  );
};

export default MarketScanSummary;
